import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'
import { Skeleton } from '@/components/ui/skeleton'
import { getNotificationStatisticDashboard } from '@/modules/Dashboard/services'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { NotificationTab } from '../../../types'
import NotificationContent from './NotificationContent'
import NotificationSkeleton from '@/components/skeletons/NotificationSkeleton'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const Notifications: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const [activeTab, setActiveTab] = useState<
        'NOTIFICATION' | 'REGULATION' | 'OTHER'
    >('NOTIFICATION')

    const tabs: NotificationTab[] = [
        {
            key: 'NOTIFICATION',
            label: t('notifications.tab.notification'),
        },
        {
            key: 'REGULATION',
            label: t('notifications.tab.regulation'),
        },
        { key: 'OTHER', label: t('notifications.tab.other') },
    ]

    const { data: notificationStatistic, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.NOTIFICATION_STATISTIC],
            queryFn: () => getNotificationStatisticDashboard(),
        },
        {
            errorDisplayMode: 'page',
        }
    )

    // Helper function to get count from statistic - Fixed type
    const getNotificationCount = (
        tabKey: 'NOTIFICATION' | 'REGULATION' | 'OTHER'
    ): number => {
        if (!notificationStatistic?.result) return 0

        // Map QD to QĐ for API compatibility
        const statisticKey = tabKey
        const statisticData =
            notificationStatistic.result[
                statisticKey as keyof typeof notificationStatistic.result
            ]

        if (
            statisticData &&
            typeof statisticData === 'object' &&
            'total_count' in statisticData
        ) {
            return (statisticData as any).total_count || 0
        }

        return 0
    }

    const stateComponent = renderState()
    if (stateComponent) {
        return (
            <div className="">
                {/* Header Skeleton */}
                <Skeleton className="h-5 w-32 mb-2" />

                {/* Tab Navigation Skeleton */}
                <div className="flex items-center mb-2 gap-2">
                    <div className="flex gap-2 p-2">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-8 w-24" />
                        ))}
                    </div>
                </div>

                {/* Content Skeleton */}
                <NotificationSkeleton count={3} />
            </div>
        )
    }

    return (
        <div className="">
            {/* Header */}
            <Typogaphy className="font-semibold text-primary mb-2">
                {t('notifications.title')}
            </Typogaphy>

            {/* Tab Navigation */}
            <div className="flex items-center mb-2 gap-2">
                <ButtonGroupCustomized
                    className="p-2"
                    active={activeTab}
                    onChange={(value) =>
                        setActiveTab(
                            value as 'NOTIFICATION' | 'REGULATION' | 'OTHER'
                        )
                    }
                    buttons={tabs?.map((tab) => ({
                        label: tab.label,
                        value: tab.key,
                        count: getNotificationCount(tab.key),
                    }))}
                />
            </div>

            {/* Content Area */}
            <NotificationContent notification={activeTab} />
        </div>
    )
}

export default Notifications
